/**
 * CategoryMoveMenu — "move to…" (chat-thread experience PR-5, 2026-09-09;
 * built-ins first-class 2026-09-09): the viewer files a thread under ANY of
 * their sections — the built-ins (direct messages, group threads, threads)
 * beside their own categories — or under none (its natural section), or a
 * new one typed inline. One list body (`CategoryMoveList`) wears three
 * chromes: the header ⋯ menu's second level (docked / phone), the wide
 * header's folder button (`CategoryMoveButton`), and a row's ⋯ menu on the
 * dark rail (`dark`). Per user only — nothing here is visible to anyone else.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { HeaderIconButton, HeaderOverflowMenu, HeaderOverflowMenuItem } from './styles';
import { CategoryFolderGlyph } from './ThreadTypeGlyph';
import { RailMenuHeading, RailMenuInput, RailMenuItem } from './organizeStyles';
import { threadListCopy } from './threadListCopy';
import {
  THREAD_CATEGORY_NAME_MAX,
  categoryLabel,
  type ThreadCategory,
  type ThreadListLang,
} from '../../types/threadCategories';

export interface CategoryMoveProps {
  /** Every section in the viewer's order (custom + built-ins). */
  categories?: readonly ThreadCategory[] | null;
  /** The section the thread is explicitly filed under (aliases resolved), or null. */
  currentCategoryId?: string | null;
  /** The built-in an UNFILED thread falls into — reads as checked when nothing is filed. */
  naturalSectionId?: string | null;
  onMoveToCategory: (categoryId: string | null) => void;
  /** Create a category by name (folding into an existing one); resolves its id (null = refused/empty). */
  onCreateCategory?: (name: string) => Promise<string | null>;
  lang?: ThreadListLang;
}

const CategoryNameInput = styled.input`
  width: 100%;
  margin: 2px 0 4px;
  padding: 7px 10px;
  border: 1px solid rgba(198, 167, 94, 0.45);
  border-radius: 6px;
  background: #ffffff;
  color: var(--brand-black);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12.5px;
  outline: none;

  &:focus {
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 2px rgba(198, 167, 94, 0.2);
  }
`;

const MoveToHeading = styled.div`
  padding: 6px 10px 4px;
  color: var(--brand-text-secondary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
`;

export interface CategoryMoveListProps extends CategoryMoveProps {
  threadTitle?: string;
  /** Called after a pick / a successful create (the host chrome closes). */
  onDone: () => void;
  /** The dark-rail chrome (a row's ⋯ menu). */
  dark?: boolean;
}

/** The list body: heading, "no category", every section (the current one checked), an inline "new category…" field. */
export const CategoryMoveList: React.FC<CategoryMoveListProps> = ({
  categories,
  currentCategoryId,
  naturalSectionId,
  onMoveToCategory,
  onCreateCategory,
  threadTitle,
  onDone,
  dark,
  lang = 'en',
}) => {
  const copy = threadListCopy(lang);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creating, setCreating] = useState(false);
  const Item = dark ? RailMenuItem : HeaderOverflowMenuItem;
  const Heading = dark ? RailMenuHeading : MoveToHeading;
  const Input = dark ? RailMenuInput : CategoryNameInput;
  const checkedId = currentCategoryId ?? naturalSectionId ?? null;
  const pick = (id: string | null) => () => {
    onMoveToCategory(id);
    onDone();
  };
  const submit = async () => {
    const name = newCategoryName.trim().slice(0, THREAD_CATEGORY_NAME_MAX);
    if (!name || !onCreateCategory || creating) return;
    setCreating(true);
    try {
      const id = await onCreateCategory(name);
      if (id) {
        onMoveToCategory(id);
        setNewCategoryName('');
        onDone();
      }
    } finally {
      setCreating(false);
    }
  };
  return (
    <>
      <Heading data-category-move-list="">{copy.moveTo(threadTitle ?? '')}</Heading>
      <Item
        type="button"
        role="menuitemradio"
        aria-checked={checkedId === null}
        data-header-overflow-category="none"
        onClick={pick(null)}
      >
        <CategoryFolderGlyph size={14} active={checkedId === null} />
        {copy.noCategory}
      </Item>
      {(categories ?? []).map((cat) => (
        <Item
          key={cat.id}
          type="button"
          role="menuitemradio"
          aria-checked={checkedId === cat.id}
          data-header-overflow-category={cat.id}
          data-builtin-section={cat.builtin ?? undefined}
          onClick={pick(cat.id)}
        >
          <CategoryFolderGlyph size={14} active={checkedId === cat.id} />
          {categoryLabel(cat, lang)}
        </Item>
      ))}
      {onCreateCategory && (
        <Input
          type="text"
          data-header-overflow-new-category=""
          aria-label={copy.newCategory}
          placeholder={copy.newCategoryPlaceholder}
          maxLength={THREAD_CATEGORY_NAME_MAX}
          value={newCategoryName}
          disabled={creating}
          onChange={(e) => setNewCategoryName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              void submit();
            }
          }}
        />
      )}
    </>
  );
};

export interface CategoryMoveButtonProps extends CategoryMoveProps {
  threadTitle: string;
}

/**
 * The wide header's folder button + its body-portaled move-to menu. The
 * glyph matches its neighbours (bell / pin / archive / add people — the same
 * muted stroke, never a gold "filed" tint, 2026-09-09); the section the
 * thread is filed under rides the tooltip and the accessible name only.
 */
export const CategoryMoveButton: React.FC<CategoryMoveButtonProps> = ({ threadTitle, ...moveTo }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: 8, top: 56 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const lang = moveTo.lang ?? 'en';
  const copy = threadListCopy(lang);

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    if (refocus) buttonRef.current?.focus();
  }, []);

  const toggle = useCallback(() => {
    if (open) {
      close(false);
      return;
    }
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect && typeof window !== 'undefined') {
      const menuWidth = 208;
      setPos({
        left: Math.max(8, Math.min(rect.right - menuWidth, (window.innerWidth || menuWidth) - menuWidth - 8)),
        top: Math.max(8, rect.bottom + 6),
      });
    }
    setOpen(true);
  }, [open, close]);

  useEffect(() => {
    if (!open) return undefined;
    menuRef.current?.querySelector<HTMLElement>('[role="menuitemradio"]')?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close(true);
    };
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      close(false);
    };
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('mousedown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('mousedown', onPointerDown, true);
    };
  }, [open, close]);

  const current = (moveTo.categories ?? []).find((c) => c.id === moveTo.currentCategoryId) ?? null;
  const currentLabel = current ? categoryLabel(current, lang) : '';
  return (
    <>
      <HeaderIconButton
        ref={buttonRef}
        data-header-move-to-btn=""
        data-current-category={current?.id ?? undefined}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={current ? `${copy.filedUnder(currentLabel)} — ${copy.moveThread(threadTitle)}` : copy.moveThread(threadTitle)}
        title={current ? copy.filedUnder(currentLabel) : copy.moveTo('')}
        onClick={toggle}
      >
        <CategoryFolderGlyph size={14} active={false} stroke="currentColor" />
      </HeaderIconButton>
      {open && typeof document !== 'undefined' && createPortal(
        <HeaderOverflowMenu
          ref={menuRef}
          role="menu"
          aria-label={copy.moveThread(threadTitle)}
          data-category-move-menu=""
          data-prest0n-portal="true"
          $left={pos.left}
          $top={pos.top}
        >
          <CategoryMoveList {...moveTo} threadTitle={threadTitle} onDone={() => close(false)} />
        </HeaderOverflowMenu>,
        document.body,
      )}
    </>
  );
};

export default CategoryMoveButton;
